import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ListSubheader from '@mui/material/ListSubheader';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { FilterCondition } from '../../types';
import { FILTER_OPERATORS, OPERATORS_BY_TYPE } from '../../constants';
import { toDisplayName, getDimensionsByGroup } from '../../utils';
import { getDimDataType, getEnumValues } from '../../utils/workspaceHelpers';

export default function FilterConditionRow({
  condition,
  semanticModel,
  getDisplayName,
  onRemove,
  locked = false,
}: {
  condition: FilterCondition;
  semanticModel: any;
  getDisplayName: (name: string) => string;
  onRemove: () => void;
  locked?: boolean;
}) {
  const updateFilterCondition = useVoyagerStore((s) => s.updateFilterCondition);
  const dimType = getDimDataType(condition.column, semanticModel);
  const operators = OPERATORS_BY_TYPE[dimType] || FILTER_OPERATORS;
  const needsNoValue = condition.operator === 'IS NULL' || condition.operator === 'IS NOT NULL';
  const isBetween = condition.operator === 'BETWEEN';
  const enumValues = getEnumValues(condition.column, semanticModel);
  const isLikeOp = condition.operator === 'LIKE' || condition.operator === 'NOT LIKE';
  const showEnumDropdown = enumValues !== null && !needsNoValue && !isLikeOp;
  const isMultiSelect = condition.operator === 'IN' || condition.operator === 'NOT IN';

  const handleDimChange = (col: string) => {
    const newType = getDimDataType(col, semanticModel);
    const newOps = OPERATORS_BY_TYPE[newType] || FILTER_OPERATORS;
    const opStillValid = newOps.find((o) => o.value === condition.operator);
    updateFilterCondition(condition.id, {
      column: col,
      value: '',
      value2: undefined,
      ...(opStillValid ? {} : { operator: newOps[0]?.value || '=' }),
    });
  };

  const handleOperatorChange = (newOp: string) => {
    const oldOp = condition.operator;
    const wasMulti = oldOp === 'IN' || oldOp === 'NOT IN';
    const willBeMulti = newOp === 'IN' || newOp === 'NOT IN';
    const newEnumValues = getEnumValues(condition.column, semanticModel);
    const newIsLike = newOp === 'LIKE' || newOp === 'NOT LIKE';
    const newNeedsNoValue = newOp === 'IS NULL' || newOp === 'IS NOT NULL';

    if (newNeedsNoValue || newIsLike || !newEnumValues) {
      updateFilterCondition(condition.id, { operator: newOp, value: '', value2: undefined });
      return;
    }

    let newValue = condition.value;
    if (wasMulti && !willBeMulti) {
      const parts = condition.value.split(',').map(v => v.trim()).filter(Boolean);
      newValue = parts[0] || '';
    }
    updateFilterCondition(condition.id, { operator: newOp, value: newValue, value2: undefined });
  };

  const selectedMultiValues = condition.value
    ? condition.value.split(',').map(v => v.trim()).filter(Boolean)
    : [];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        ...(locked ? {
          border: '1px dashed',
          borderColor: 'action.disabled',
          borderRadius: 1,
          bgcolor: 'action.hover',
          px: 0.75,
          py: 0.25,
          opacity: 0.85,
        } : {}),
      }}
    >
      {locked && (
        <Tooltip title="Default filter (from model definition)">
          <LockIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.25 }} />
        </Tooltip>
      )}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <Select
          value={condition.column}
          displayEmpty
          disabled={locked}
          onChange={(e) => handleDimChange(e.target.value)}
          sx={{ fontSize: 12 }}
        >
          <MenuItem value="" disabled><em>Dimension</em></MenuItem>
          {(semanticModel ? getDimensionsByGroup(semanticModel) : []).flatMap((group) => [
            <ListSubheader key={`hdr:${group.label}`} sx={{ fontSize: 11, lineHeight: '28px', fontWeight: 700, color: 'text.secondary', bgcolor: 'var(--sunk)' }}>
              {group.label}
            </ListSubheader>,
            ...group.dimensions.map((d) => (
              <MenuItem key={d.name} value={d.name} sx={{ fontSize: 12, pl: 3 }}>{getDisplayName(d.name)}</MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 110 }}>
        <Select
          value={condition.operator}
          disabled={locked}
          onChange={(e) => handleOperatorChange(e.target.value)}
          sx={{ fontSize: 12 }}
        >
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value} sx={{ fontSize: 12 }}>{op.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {showEnumDropdown && isMultiSelect && enumValues && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: 1, minWidth: 180 }}>
          <Autocomplete
            multiple
            size="small"
            disabled={locked}
            disableCloseOnSelect
            options={enumValues}
            value={selectedMultiValues}
            onChange={(_e, newVal) => {
              updateFilterCondition(condition.id, { value: newVal.join(', ') });
            }}
            getOptionLabel={(option) => toDisplayName(option)}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option} style={{ fontSize: 12 }}>
                <Checkbox size="small" checked={selected} sx={{ mr: 0.5, p: 0.25 }} />
                {toDisplayName(option)}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={toDisplayName(option)}
                  size="small"
                  sx={{ height: 20, fontSize: 11, '& .MuiChip-label': { px: 0.5 } }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder={selectedMultiValues.length === 0 ? 'Select values' : ''} sx={{ '& input': { fontSize: 12 } }} />
            )}
            sx={{ flex: 1, minWidth: 150 }}
          />
          {!locked && (
            <>
              <Tooltip title="Select All">
                <IconButton
                  size="small"
                  onClick={() => updateFilterCondition(condition.id, { value: enumValues.join(', ') })}
                  sx={{ p: 0.25 }}
                >
                  <AddIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear All">
                <IconButton
                  size="small"
                  onClick={() => updateFilterCondition(condition.id, { value: '' })}
                  sx={{ p: 0.25 }}
                >
                  <DeleteSweepIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )}
      {showEnumDropdown && !isMultiSelect && enumValues && (
        <Autocomplete
          size="small"
          disabled={locked}
          options={enumValues}
          value={condition.value || null}
          onChange={(_e, newVal) => {
            updateFilterCondition(condition.id, { value: newVal || '' });
          }}
          getOptionLabel={(option) => toDisplayName(option)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select value" sx={{ '& input': { fontSize: 12 } }} />
          )}
          sx={{ minWidth: 150 }}
        />
      )}
      {!showEnumDropdown && !needsNoValue && (
        <TextField
          size="small"
          disabled={locked}
          placeholder={isBetween ? 'From' : 'Value'}
          type={dimType === 'date' ? 'date' : dimType === 'number' ? 'number' : 'text'}
          value={condition.value}
          onChange={(e) => updateFilterCondition(condition.id, { value: e.target.value })}
          sx={{ minWidth: 90, '& input': { fontSize: 12 } }}
          InputLabelProps={dimType === 'date' ? { shrink: true } : undefined}
        />
      )}
      {isBetween && (
        <TextField
          size="small"
          disabled={locked}
          placeholder="To"
          type={dimType === 'date' ? 'date' : dimType === 'number' ? 'number' : 'text'}
          value={condition.value2 || ''}
          onChange={(e) => updateFilterCondition(condition.id, { value2: e.target.value })}
          sx={{ minWidth: 90, '& input': { fontSize: 12 } }}
          InputLabelProps={dimType === 'date' ? { shrink: true } : undefined}
        />
      )}
      {!locked && (
        <IconButton size="small" onClick={onRemove} sx={{ p: 0.25 }}>
          <DeleteOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </IconButton>
      )}
    </Box>
  );
}
